import type { Project } from "./Project";
import { projectTracingImages } from "./Project";

/** Prefix of a server-stored image URL — the Worker's account-scoped image
 *  API. Everything after it is the R2 object key (`<account>/<uuid>`).
 *  Local-only images use `data:` URLs instead and never appear here. */
const API_IMAGE_PREFIX = "/api/images/";

/** Every R2 object key the project currently references: each tracing
 *  image's original (`url`) and 200-DPI proxy (`finalUrl`) when they're
 *  server-stored. Duplicated images share keys, so this is a set — a key
 *  stays "referenced" until the LAST image using it is gone. */
export function collectServerImageKeys(project: Project): Set<string> {
  const keys = new Set<string>();
  for (const img of projectTracingImages(project)) {
    for (const url of [img.url, img.finalUrl]) {
      if (url?.startsWith(API_IMAGE_PREFIX)) {
        keys.add(url.slice(API_IMAGE_PREFIX.length));
      }
    }
  }
  return keys;
}

/**
 * Deferred, undo-safe garbage collection of server-stored images
 * (issue #952): when the user deletes a tracing image (and nothing else
 * references its R2 objects), the original and the 200-DPI proxy should
 * be deleted from the server — but NOT immediately, because undo restores
 * the image verbatim and would then point at dead URLs.
 *
 * `onStateChange` runs on every history change (commit / undo / redo /
 * replace) and diffs the referenced-key set: keys that vanish become
 * *pending*; keys that reappear (undo, redo, paste) are un-pended. The
 * actual `DELETE /api/images/<key>` calls fire only in `flush()`, wired
 * to real page teardown — in-memory history dies with the page and the
 * autosave persists only the current state, so at that point "still
 * needed" is exactly "referenced by the current state". Best-effort by
 * design: a crashed tab leaks the objects until a later session deletes
 * the image again.
 *
 * Saved `.leather` files are immune: export inlines server-stored
 * images as data URLs (`inlineServerImages` in storage/projectFile.ts),
 * so a file written before a deletion keeps its images even after the
 * server objects are collected.
 */
export class ServerImageGc {
  private prevKeys: Set<string>;
  private pending = new Set<string>();

  constructor(initial: Project) {
    this.prevKeys = collectServerImageKeys(initial);
  }

  onStateChange(project: Project): void {
    const now = collectServerImageKeys(project);
    for (const key of this.prevKeys) {
      if (!now.has(key)) this.pending.add(key);
    }
    for (const key of now) this.pending.delete(key);
    this.prevKeys = now;
  }

  /** Keys queued for deletion — exposed for tests. */
  get pendingKeys(): ReadonlySet<string> {
    return this.pending;
  }

  /** Fire the queued deletions. Call on real page teardown only
   *  (`pagehide` / `beforeunload`) — never on mere tab-backgrounding,
   *  where the user may come back and undo. `keepalive` lets the
   *  requests outlive the unloading document. */
  flush(): void {
    for (const key of this.pending) {
      void fetch(`${API_IMAGE_PREFIX}${key}`, {
        method: "DELETE",
        keepalive: true,
      }).catch(() => {});
    }
    this.pending.clear();
  }
}
